'use client';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { format } from 'date-fns';
import ContactCTA from '@/components/ContactCTA';
import ContactNew from '@/components/ContactNew';
import Content from '@/components/Content';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions';
import Header from '@/components/Header';
import RelatedNews from '@/components/RelatedNews';
import RelatedSolutions from '@/components/RelatedSolutions';
import { Markdown } from '@/lib/markdown';
import { INews } from '@/models/news';
import { IRelatedSolution, IWarehousingSolution } from '@/models/warehousingSolution';

export default function GeneralPage(props: {
	data: IWarehousingSolution;
	className?: string;
	preview?: boolean;
	showContactCta?: boolean;
	relatedSolutions?: IRelatedSolution[];
	/** Recent articles, shown when the entry has no related posts of its own. */
	recentPosts?: INews[];
}) {
	const data = props.data;
	// Solutions pages show related Solutions in place of the Related news section.
	const hasRelatedSolutions = !!props.relatedSolutions?.length;
	// Articles curated on the entry win; otherwise fall back to the latest news,
	// so every article page carries the section (LIND-57).
	const relatedPosts = data?.relatedPostsCollection?.items?.length
		? data.relatedPostsCollection.items
		: (props.recentPosts ?? []);
	const hasRelatedPosts = !hasRelatedSolutions && !!relatedPosts.length;

	if (!data) {
		return null;
	}

	return (
		<section className="-mt-16">
			<div className="z-20 relative bg-contain xl:bg-cover bg-white text-white bg-bottom flex flex-col">
				<Header
					small
					title={data?.title}
					headerDescription={
						data?.date ? format(new Date(data?.date), 'MMMM do, yyyy') : undefined
					}
					headerSliderImagesCollection={{ items: [data?.cardImage] }}
				/>
			</div>

			<div
				className={`z-20 relative black container pt-12 md:pt-24 flex flex-col [&_a]:text-primary-500 [&_a]:font-bold [&_a:hover]:underline ${
					hasRelatedSolutions || hasRelatedPosts ? 'mb-8 lg:mb-12' : 'mb-20 lg:mb-32'
				} ${props.className ? props.className : ''}`}
			>
				<Markdown className=" [&_p]:lg:text-xl [&_p]:text-lg " content={data.content} />
			</div>

			{hasRelatedSolutions ? (
				<div className="py-12 md:py-16">
					<RelatedSolutions solutions={props.relatedSolutions ?? []} />
				</div>
			) : null}

			{hasRelatedPosts ? (
				<div className="py-12 md:py-16">
					<RelatedNews articles={relatedPosts} />
				</div>
			) : null}

			{props.showContactCta !== false ? <ContactCTA /> : null}

			{data?.faqsCollection?.items?.length ? (
				<div className="z-10 relative pt-6 pb-12 md:pt-12 md:pb-32 flex flex-col overflow-clip bg-white">
					<FrequentlyAskedQuestions
						faqs={data?.faqsCollection?.items}
						title={'Frequently Asked Questions'}
					/>
				</div>
			) : null}
		</section>
	);
}
